import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api, { isDesktop, userCacheKey } from '../lib/api';
import { initDesktopToken, getDesktopToken, setDesktopToken, clearDesktopToken } from '../lib/desktopToken';
import { initDesktopDevice, getDeviceIdentity } from '../lib/desktopDevice';
import { canResumeDesktopSession } from '../lib/desktopSession';
import {
  isShiftLocked, setShiftLocked, cacheVerifier, clearShiftLockState,
  verifyPinOffline, bumpOfflineFail, MAX_OFFLINE_FAILS,
} from '../lib/shiftLock';
import { startDesktopIdleWatch } from '../lib/desktopIdleWatch';
import { saveLastSignin } from '../lib/desktopMemory';
import { enrollQuickSignin } from '../lib/quickSignin';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const authGeneration = useRef(0);
  const userRef = useRef(null);
  userRef.current = user;
  const [locked, setLocked] = useState(() => isShiftLocked());
  const [activeBranch, setActiveBranch] = useState(() => {
    try { return JSON.parse(localStorage.getItem('pharmxai_active_branch')); } catch { return null; }
  });

  const checkAuth = useCallback(async () => {
    const generation = ++authGeneration.current;
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const path = window.location.pathname;
    const publicAuthRoute = path === '/login' || path === '/plans' || path === '/welcome' || path === '/signup/pharmacy' || path === '/try-demo' || path.startsWith('/partners/') || path.startsWith('/b/');
    if (publicAuthRoute && !(isDesktop() && path === '/login')) {
      setLoading(false);
      return;
    }

    // Desktop keeps its Bearer token in the Electron main process (OS
    // keychain) — hydrate it, and the terminal identity, before the first
    // authenticated call.
    let token = null;
    let device = null;
    let savedUser = null;
    try {
      if (isDesktop()) [token, device] = await Promise.all([initDesktopToken(), initDesktopDevice()]);
      savedUser = JSON.parse(localStorage.getItem(userCacheKey()) || 'null');
    } catch { /* Missing or unreadable credentials cannot resume offline. */ }
    const canResume = isDesktop()
      ? canResumeDesktopSession(token, savedUser, device)
      : !!savedUser;

    try {
      if (isDesktop() && !navigator.onLine) throw new Error('Terminal is offline');
      const res = await api.get('/auth/me', { timeout: 8000 });
      if (generation !== authGeneration.current) return;
      setUser(res.data);
      localStorage.setItem(userCacheKey(), JSON.stringify(res.data));
      localStorage.setItem('pharmxai_last_online_check', String(Date.now()));
      // Cache the offline PIN verifier so a till inside its offline window can
      // still unlock itself; drop it the moment the PIN is removed.
      if (res.data.pin_set) {
        try {
          const { data } = await api.get('/auth/shift-pin');
          cacheVerifier(data.verifier);
        } catch { /* a missing verifier only costs the offline unlock path */ }
      } else {
        cacheVerifier(null);
        setShiftLocked(false);
      }
    } catch (err) {
      if (generation !== authGeneration.current) return;
      const rejected = [401, 403].includes(err.response?.status);
      const unavailable = !err.response || err.response.status >= 500;
      if (!rejected && unavailable && canResume) {
        setUser(savedUser);
      } else {
        if (rejected || !canResume) {
          await clearDesktopToken();
          clearShiftLockState();
          localStorage.removeItem(userCacheKey());
          setLocked(false);
        }
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Promptly re-validate / renew the session as soon as connectivity returns,
  // instead of waiting for the next page navigation.
  useEffect(() => {
    window.addEventListener('online', checkAuth);
    return () => window.removeEventListener('online', checkAuth);
  }, [checkAuth]);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
      localStorage.setItem(userCacheKey(), JSON.stringify(res.data));
      localStorage.setItem('pharmxai_last_online_check', String(Date.now()));
      return res.data;
    } catch {
      return null;
    }
  }, []);

  const switchBranch = useCallback(async (branchId, branchName) => {
    try {
      if (!branchId || branchId === 'all') {
        await api.post('/tenants/clear-selection');
        setActiveBranch(null);
        localStorage.removeItem('pharmxai_active_branch');
        saveLastSignin({ branch: null });
      } else {
        await api.post(`/tenants/${branchId}/select`);
        const branch = { tenant_id: branchId, name: branchName };
        setActiveBranch(branch);
        localStorage.setItem('pharmxai_active_branch', JSON.stringify(branch));
        saveLastSignin({ branch });
      }
      await refreshUser();
      return true;
    } catch {
      return false;
    }
  }, [refreshUser]);

  const completeLogin = async (res) => {
    ++authGeneration.current;
    const { token, user: userData } = res.data;
    // Browser web app relies solely on the httpOnly `session_token` cookie
    // the backend just set — nothing sensitive goes into localStorage here.
    // Desktop (cross-origin to the cloud API) still needs the Bearer token.
    if (isDesktop()) await setDesktopToken(token);
    clearShiftLockState();
    setLocked(false);
    if (userData.pin_set) {
      try {
        const { data } = await api.get('/auth/shift-pin', { timeout: 8000 });
        cacheVerifier(data.verifier);
      } catch { /* Never reuse a previous staff member's offline verifier. */ }
    }
    localStorage.setItem(userCacheKey(), JSON.stringify(userData));
    localStorage.setItem('pharmxai_last_online_check', String(Date.now()));
    setUser(userData);
    return userData;
  };

  const login = async (email, password) => {
    if (isDesktop()) await initDesktopDevice();
    const userData = await completeLogin(await api.post('/auth/login', { email, password }));
    await enrollQuickSignin(userData);
    return userData;
  };

  const quickLogin = async (cred, pin) => completeLogin(
    await api.post('/auth/quick-signin', { cred_id: cred.cred_id, secret: cred.secret, pin }),
  );

  const demoLogin = async (role) => completeLogin(await api.post('/demo/start', { role }));

  const register = async (email, password, name) => {
    const res = await api.post('/auth/register', { email, password, name });
    const { token, user: userData } = res.data;
    if (isDesktop()) setDesktopToken(token);
    localStorage.setItem(userCacheKey(), JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  // Remote access per branch — a web-only sign-in from a branch's dedicated
  // /b/{slug} link, never from the desktop app. Owners land with full access;
  // staff land read-only (enforced server-side, see auth.py).
  const remoteLogin = async (slug, email, password) => {
    const res = await api.post(`/public/branches/${slug}/login`, { email, password });
    const userData = res.data.user;
    localStorage.setItem(userCacheKey(), JSON.stringify(userData));
    localStorage.setItem('pharmxai_last_online_check', String(Date.now()));
    setUser(userData);
    return userData;
  };

  const loginWithGoogle = (landing = '/dashboard') => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + (typeof landing === 'string' ? landing : '/dashboard');
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = useCallback(async () => {
    ++authGeneration.current;
    const signOutPath = userRef.current?.tenant_id === 'tenant_demo_workspace' ? '/try-demo' : '/login';
    // Remove local access immediately, even if the network is unavailable.
    const request = api.post('/auth/logout', {}, { timeout: 5000 });
    await clearDesktopToken();
    clearShiftLockState();
    localStorage.removeItem(userCacheKey());
    localStorage.removeItem('pharmxai_active_branch');
    localStorage.removeItem('pharmxai_last_online_check');
    setLocked(false);
    setUser(null);
    setActiveBranch(null);
    try {
      await request;
    } catch { /* ignore */ }
    return signOutPath;
  }, []);

  const exitImpersonation = useCallback(async () => {
    await api.post('/auth/impersonation/exit');
    clearDesktopToken();
    localStorage.removeItem(userCacheKey());
    localStorage.removeItem('pharmxai_active_branch');
    window.location.assign('/platform');
  }, []);

  // ── Shift lock ──────────────────────────────────────────
  // Locking keeps the session alive and simply covers the screen, so a
  // handover costs four taps instead of a full sign-in — and it works on a
  // till that is offline.
  const lockShift = useCallback(() => {
    setShiftLocked(true);
    setLocked(true);
  }, []);

  const unlockShift = useCallback(async (pin) => {
    if (navigator.onLine) {
      try {
        await api.post('/auth/shift-unlock', { pin });
        setShiftLocked(false);
        setLocked(false);
        return { ok: true };
      } catch (err) {
        if (err.response?.data?.force_logout) {
          await logout();
          return { ok: false, message: err.response.data.detail };
        }
        if (err.response && err.response.status < 500) return { ok: false, message: err.response.data?.detail || 'Wrong PIN' };
        // No response at all — we're offline, fall through to the local check.
      }
    }
    if (await verifyPinOffline(pin)) {
      setShiftLocked(false);
      setLocked(false);
      return { ok: true };
    }
    const fails = bumpOfflineFail();
    if (fails >= MAX_OFFLINE_FAILS) {
      await logout();
      return { ok: false, message: 'Too many wrong PINs — signed out.' };
    }
    return { ok: false, message: `Wrong PIN — ${MAX_OFFLINE_FAILS - fails} attempt(s) left` };
  }, [logout]);

  // Desktop terminals hold a 14-day session so they survive an outage. That
  // is a tolerance for being offline, not an inactivity policy — while the
  // terminal is online, an unattended till signs out on the shorter idle
  // timeout instead.
  useEffect(() => {
    if (!user || !isDesktop()) return undefined;
    return startDesktopIdleWatch(() => {
      logout().finally(() => { window.location.href = '/login'; });
    });
  }, [user, logout]);

  useEffect(() => {
    if (!user || !isDesktop()) return undefined;
    const timer = setInterval(() => {
      if (!canResumeDesktopSession(getDesktopToken(), user, getDeviceIdentity())) logout();
    }, 30000);
    return () => clearInterval(timer);
  }, [user, logout]);

  const setAuthUser = (userData) => {
    setUser(userData);
    localStorage.setItem(userCacheKey(), JSON.stringify(userData));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, quickLogin, demoLogin, register, remoteLogin, loginWithGoogle, logout, exitImpersonation, setAuthUser, refreshUser, activeBranch, switchBranch, locked, lockShift, unlockShift }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
